import { useState, useCallback, useEffect } from "react";

import "../../assets/css/app_landing.scss";
import Header from "../../components/header";
import Footer from "../../components/footer";
import NewsDetailModal from "../../components/modal/news_detail_modal";
import { getGeneralBoardList } from "../../api/board";
import { parseContent } from "../../utils/quill_parser_util";

function NewsPage() {
  const [activeTab, setActiveTab] = useState("전체");
  const [selectedNews, setSelectedNews] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newsData, setNewsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 12;
  const pageGroupSize = 5;

  const getTabType = (tab) => {
    const tabMapping = {
      전체: "ALL",
      공지사항: "NOTICE",
      이벤트: "NOTICE_EVENT",
      FAQ: "FAQ",
    };
    return tabMapping[tab] || "ALL";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}.${month}.${day}`;
  };

  const transformBoardData = (boardList) => {
    return boardList.map((item) => {
      const parsedContent = parseContent(item.content || "");

      let description = "";
      if (item.content) {
        description = parseContent(item.content);
      } else {
        description = parsedContent.substring(0, 300);
        if (parsedContent.length > 300) {
          description += "...";
        }
      }

      let extractedImage = null;
      let descriptionWithoutImage = description;

      const imgRegex = /<img[^>]+src=["']([^"'>]+)["']/i;
      const imgMatch = description.match(imgRegex);

      if (imgMatch && imgMatch[1]) {
        extractedImage = imgMatch[1];

        descriptionWithoutImage = description
          .replace(/<img[^>]*>/gi, "")
          .trim();
      }

      return {
        id: item.boardSeq,
        badge: item.detailType == "0032" ? "이벤트" : item.typeName,
        title: item.title || "",
        desc: descriptionWithoutImage || "",
        fullDesc: parsedContent,
        date: formatDate(item.regDate),
        image: extractedImage,
        originalData: item,
      };
    });
  };

  const fetchNewsList = useCallback(
    async (page) => {
      setIsLoading(true);
      try {
        const type = getTabType(activeTab);
        const result = await getGeneralBoardList(type, page - 1, pageSize);

        if (result.success && result.data) {
          const transformedData = transformBoardData(result.data);
          setNewsData(transformedData);
          setTotalPages(result.totalPages || 1);
          setTotalElements(result.totalElements || 0);
        } else {
          console.error("Failed to fetch news list:", result.error);
          setNewsData([]);
          setTotalPages(1);
          setTotalElements(0);
        }
      } catch (error) {
        console.error("Error fetching news list:", error);
        setNewsData([]);
        setTotalPages(1);
        setTotalElements(0);
      } finally {
        setIsLoading(false);
      }
    },
    [activeTab, pageSize]
  );

  useEffect(() => {
    fetchNewsList(currentPage);
  }, [currentPage, fetchNewsList]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const applyMasonryLayout = useCallback(() => {
    const container = document.querySelector(".news-grid");
    if (!container) return;

    if (window.innerWidth <= 768) {
      container.style.height = "auto";
      Array.from(container.children).forEach((child) => {
        child.style.position = "relative";
        child.style.top = "auto";
        child.style.left = "auto";
      });
      return;
    }

    const numCols = window.innerWidth <= 1024 ? 2 : 3;
    const cards = Array.from(container.children);
    const gap = 24;
    const containerWidth = container.offsetWidth;
    const cardWidth = (containerWidth - gap * (numCols - 1)) / numCols;

    const colHeights = Array(numCols).fill(0);

    cards.forEach((card) => {
      const minColIndex = colHeights.indexOf(Math.min(...colHeights));

      card.style.position = "absolute";
      card.style.width = `${cardWidth}px`;
      card.style.left = `${minColIndex * (cardWidth + gap)}px`;
      card.style.top = `${colHeights[minColIndex]}px`;

      colHeights[minColIndex] += card.offsetHeight + gap;
    });

    container.style.height = `${Math.max(...colHeights)}px`;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      applyMasonryLayout();
    }, 100);

    const recalcTimer = setTimeout(() => {
      applyMasonryLayout();
    }, 300);

    const finalTimer = setTimeout(() => {
      applyMasonryLayout();
    }, 600);

    const handleResize = () => {
      applyMasonryLayout();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timer);
      clearTimeout(recalcTimer);
      clearTimeout(finalTimer);
      window.removeEventListener("resize", handleResize);
    };
  }, [applyMasonryLayout, activeTab, newsData]);

  const handleNewsClick = (news) => {
    setSelectedNews(news);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      setSelectedNews(null);
    }, 300);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageGroup = () => {
    const startPage =
      Math.floor((currentPage - 1) / pageGroupSize) * pageGroupSize + 1;
    const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);
    return { startPage, endPage };
  };

  const { startPage, endPage } = getPageGroup();

  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const handlePrevGroup = () => {
    if (startPage > 1) {
      setCurrentPage(startPage - 1);
    }
  };

  const handleNextGroup = () => {
    if (endPage < totalPages) {
      setCurrentPage(endPage + 1);
    }
  };

  return (
    <div className="app-landing-renewed">
      <Header />

      <section className="news-section" id="news">
        <div className="news-container">
          <h2 className="section-title">키즈다 소식</h2>
          <div className="news-tabs">
            {["전체", "공지사항", "이벤트", "FAQ"].map((tab) => (
              <button
                key={tab}
                className={`tab ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="news-grid-wrapper">
            {isLoading ? (
              <div className="loading-state">
                <p>소식을 불러오는 중...</p>
              </div>
            ) : newsData.length > 0 ? (
              <div className="news-grid">
                {newsData.map((item) => (
                  <div className="notice-card" key={item.id}>
                    <p className="notice-badge">
                      <span>{item.badge}</span>
                    </p>
                    <div className="notice-date">{item.date}</div>
                    <h3 className="notice-title">{item.title}</h3>
                    {item.image && (
                      <div className="notice-image">
                        <img src={item.image} alt={item.title} />
                      </div>
                    )}
                    <p
                      className="notice-desc"
                      dangerouslySetInnerHTML={{ __html: item.desc }}
                    ></p>
                    <a
                      className="notice-link"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleNewsClick(item);
                      }}
                    >
                      자세히 보기 →
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>등록된 소식이 없습니다.</p>
              </div>
            )}
          </div>

          {!isLoading && newsData.length > 0 && totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn prev"
                onClick={handlePrevGroup}
                disabled={startPage === 1}
              >
                이전
              </button>

              <div className="pagination-numbers">
                {pageNumbers.map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`pagination-number ${
                      currentPage === pageNum ? "active" : ""
                    }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                className="pagination-btn next"
                onClick={handleNextGroup}
                disabled={endPage >= totalPages}
              >
                다음
              </button>
            </div>
          )}
        </div>
      </section>

      <Footer />

      {isModalOpen && selectedNews && (
        <NewsDetailModal news={selectedNews} onClose={handleCloseModal} />
      )}
    </div>
  );
}

export default NewsPage;
